import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card, severityStyles } from '@/components/common';
import { ChevronDown, AlertOctagon, AlertTriangle, Bell, Info, Lightbulb } from 'lucide-react';

const icons = { critical: AlertOctagon, important: AlertTriangle, attention: Bell, information: Info, opportunity: Lightbulb };
const filters = ['all', 'critical', 'important', 'attention', 'information', 'opportunity'];

const Alerts = () => {
  const { t, tx, data } = useApp();
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const list = filter === 'all' ? data.alerts : data.alerts.filter((a) => a.severity === filter);

  return (
    <div>
      <PageHeader title={t('alerts.title')} subtitle={t('alerts.subtitle')} />

      <div className="flex flex-wrap gap-2 mb-5">
        {filters.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-testid={`alert-filter-${f}`}
            className={`px-3.5 h-9 rounded-full text-sm font-medium transition-colors ${filter === f ? 'bg-zinc-900 text-white' : 'bg-white/[0.96] backdrop-blur-xl border border-white/80 text-zinc-700 hover:bg-white'}`}
          >
            {f === 'all' ? t('alerts.all') : t(`alerts.${f}`)}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {list.map((a, i) => {
          const s = severityStyles[a.severity];
          const Icon = icons[a.severity];
          const open = expanded === a.id;
          return (
            <motion.div key={a.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
              <Card className={`overflow-hidden border-l-4 ${s.border}`} data-testid={`alert-${a.id}`}>
                <button onClick={() => setExpanded(open ? null : a.id)} className="w-full flex items-start gap-4 p-4 sm:p-5 text-left">
                  <div className={`w-10 h-10 rounded-lg ${s.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${s.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${s.chip}`}>{t(`alerts.${a.severity}`)}</span>
                      <span className="text-xs text-zinc-400">{a.date}</span>
                    </div>
                    <p className="font-heading font-semibold text-zinc-900">{tx(a.title)}</p>
                    <p className="text-sm text-zinc-500 mt-0.5">{tx(a.desc)}</p>
                  </div>
                  <ChevronDown className={`w-5 h-5 text-zinc-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="px-5 pb-5">
                    <div className={`rounded-lg ${s.bg} p-4`}>
                      <p className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">{t('common.why')}</p>
                      <p className="text-sm text-zinc-700">{tx(a.why)}</p>
                    </div>
                  </motion.div>
                )}
              </Card>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default Alerts;
